/**
 * 합성 단계 사다리 (Forge a Star — 9 단계).
 *
 * 운석조각 → 별 까지 9 등급. 각 등급은 ASCII 머신 이름(`name`, kebab-case)
 * 만 보유 — 사용자 노출 라벨은 `tierDisplayName()` 가 i18n 카탈로그에서
 * 조회한다 (F-072).
 *
 * 색상 밸런스: 어두운 운석톤(회갈→적갈→러스트) → 차가운 천체톤(시안→보라
 * →은빛) → 행성+별 (에메랄드→주황→황금). 각 단계 hue 간 거리가 충분해
 * 한눈에 구분 가능.
 */

import { t } from "../i18n/index.ts";

export type TierStyle =
  | "rock-bright"
  | "ice-crystal"
  | "water-orb"
  | "patchy-world"
  | "balanced-world"
  | "earth-clone";

export interface PlanetTier {
  index: number;
  /** 머신 이름 (ASCII, kebab-case). 메시 이름·테스트·로그용 + i18n 키. */
  name: string;
  radius: number;
  mass: number;
  color: number;
  scoreValue: number;
  style: TierStyle;
  /** 0..1 — used by texture/material to interpolate toward Earth-like look. */
  earthness: number;
}

const ENTRIES: ReadonlyArray<
  Omit<PlanetTier, "mass" | "scoreValue">
> = [
  { index: 0, name: "meteor-shard", radius: 0.50, color: 0xa89a7c, style: "rock-bright",   earthness: 0.00 },
  { index: 1, name: "small-meteor", radius: 0.72, color: 0x9d6347, style: "rock-bright",   earthness: 0.05 },
  { index: 2, name: "meteor",       radius: 1.00, color: 0xc8553d, style: "rock-bright",   earthness: 0.10 },
  { index: 3, name: "comet",        radius: 1.35, color: 0x6cd4ff, style: "ice-crystal",   earthness: 0.20 },
  { index: 4, name: "asteroid",     radius: 1.75, color: 0x6c5ad6, style: "patchy-world",  earthness: 0.35 },
  { index: 5, name: "moon",         radius: 2.20, color: 0xc0bcd0, style: "patchy-world",  earthness: 0.55 },
  { index: 6, name: "planet",       radius: 2.75, color: 0x3a9c5a, style: "balanced-world", earthness: 0.80 },
  { index: 7, name: "protostar",    radius: 3.40, color: 0xff7a3a, style: "earth-clone",   earthness: 0.50 },
  { index: 8, name: "star",         radius: 4.20, color: 0xffd84a, style: "earth-clone",   earthness: 0.00 },
];

export const TIER_TABLE: ReadonlyArray<PlanetTier> = ENTRIES.map((e) => ({
  ...e,
  mass: Math.pow(e.radius / ENTRIES[0]!.radius, 2.5),
  scoreValue: e.index * e.index + 1,
}));

export function getTier(index: number): PlanetTier {
  const tier = TIER_TABLE[index];
  if (!tier) throw new Error(`unknown tier index: ${index}`);
  return tier;
}

export function maxTierIndex(): number {
  return TIER_TABLE.length - 1;
}

export function isMaxTier(index: number): boolean {
  return index === maxTierIndex();
}

/** Legacy alias — flags late-game tiers carrying the earth-clone style. */
export function isEarthTier(index: number): boolean {
  return getTier(index).style === "earth-clone";
}

/** True for the max tier — the player's win-condition body (the star). */
export function isStarTier(index: number): boolean {
  return index === maxTierIndex();
}

/** Looks up the user-facing label for a tier in the active locale. */
export function tierDisplayName(tier: PlanetTier): string {
  return t(`tier.${tier.name}.name`);
}

export const __testing = {
  TIER_NAMES: ENTRIES.map((e) => e.name),
};
